import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ComboboxPackageSelect } from '../../../../../components/ComboboxPackageSelect'
import { RESERVED_PACKAGE_OPTIONS } from '../RecommendedPackageSection/constants'
import { getAvailablePackingOptions } from '../RecommendedPackageSection/utils'
import { orderShippingPackagesByPriority } from './utils'
import { PACKAGE_OPTIONS } from '../RecommendedPackageSection/constants'

const PackageSelect = ({
  isPackagelessOrder,
  hasPackagelessProduct,
  storePackingOptions,
  availableShippingPackages,
  onShippingPackageChange,
  isFragileOrder,
  title = null,
  ignoreFragileType = false,
  initialShippingPackage = null,
  orderStoreId = null,
  shouldAutofocus = false,
  autofocusSignal = 0,
}) => {
  const { t } = useTranslation()
  const availableStoreShippingPackages = useMemo(() => {
    const packingOptions = getAvailablePackingOptions(
      storePackingOptions,
      isPackagelessOrder,
      isFragileOrder,
      RESERVED_PACKAGE_OPTIONS,
      false,
      true,
      ignoreFragileType,
    )

    if (!isPackagelessOrder && hasPackagelessProduct) {
      const packagelessOption = Object.keys(storePackingOptions)?.find(option => option === PACKAGE_OPTIONS.PACKAGELESS)

      if (packagelessOption) {
        packingOptions.push(packagelessOption)
      }
    }

    const filtered = orderShippingPackagesByPriority(availableShippingPackages, packingOptions)
    const safeFiltered = Array.isArray(filtered) ? filtered : []
    // Config-mismatch escape hatch — NOT a general path.
    //
    // If the store's packing-options config doesn't intersect the order's
    // available shipping packages (seen on the CL warehouse — store
    // options and package_type values didn't line up), the combobox
    // would end up empty and the operator couldn't pick ANY empaque on
    // a new caja. Falling back to the raw available list unblocks them.
    //
    // We intentionally DON'T fall back for orders where the empty result
    // is safety-critical — fragile and packageless. For those, we'd
    // rather surface the misconfig (empty combobox) than silently offer
    // packages that could damage the product or violate the packageless
    // constraint. Reserved options are also enforced by the filter, so
    // keeping fragile/packageless out of the fallback preserves that too.
    const canFallbackSafely = !isFragileOrder && !isPackagelessOrder && !hasPackagelessProduct
    if (safeFiltered.length === 0 && canFallbackSafely && Array.isArray(availableShippingPackages) && availableShippingPackages.length > 0) {
      return availableShippingPackages
    }
    return safeFiltered
  }, [availableShippingPackages, isFragileOrder, hasPackagelessProduct, storePackingOptions, isPackagelessOrder, ignoreFragileType])

  // The server already filtered the list down to the brand's allowed boxes, so an empty
  // array means the brand has none here. undefined/null means the list is still loading.
  const brandHasNoAllowedPackages = Array.isArray(availableShippingPackages) && availableShippingPackages.length === 0

  return (
    <div className="flex justify-between">
      {title !== null && <span className="font-medium text-lg flex items-center">{title}</span>}

      {brandHasNoAllowedPackages ? (
        <div className={`${title ? 'w-10/12' : 'w-full'} bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-700 font-medium`}>
          {t('generate_shipment.shipping_package_data.no_allowed_packages_for_store')}
        </div>
      ) : (
        <ComboboxPackageSelect
          options={availableStoreShippingPackages}
          value={initialShippingPackage}
          className={title ? 'w-10/12' : 'w-full'}
          optionsStyles={'text-xl font-medium'}
          onChange={onShippingPackageChange}
          autoFocusOnMount={shouldAutofocus}
          autoFocusSignal={autofocusSignal}
        />
      )}
    </div>
  )
}

export default PackageSelect
